import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  LayoutDashboard, 
  FileText, 
  Newspaper, 
  Image, 
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  Shield,
  BarChart3,
  BookOpen,
  MapPin,
  Users,
  FileEdit,
  MessageSquare,
  FileBarChart,
  BookMarked,
  Mail
} from 'lucide-react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';

const Layout = styled.div`
  display: flex;
  min-height: 100vh;
  background: var(--background-light);
`;

const Sidebar = styled.aside`
  width: 280px;
  background: linear-gradient(180deg, #001a45 0%, #002664 100%);
  color: var(--white);
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: 1000;
  transition: var(--transition);

  @media (max-width: 1024px) {
    transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
  }
`;

const SidebarHeader = styled.div`
  padding: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;

  .logo {
    width: 50px;
    height: 50px;
    background: var(--accent-gold);
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-blue);
  }

  h2 {
    font-family: var(--font-secondary);
    font-size: 1.1rem;
    font-weight: 700;
  }

  p {
    font-size: 0.8rem;
    opacity: 0.7;
  }
`;

const Nav = styled.nav`
  padding: 1.5rem 1rem;
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  border-radius: var(--radius-md);
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
  margin-bottom: 0.5rem;
  transition: var(--transition);

  svg {
    opacity: 0.7;
  }

  &:hover, &.active {
    background: rgba(255, 255, 255, 0.1);
    color: var(--white);

    svg {
      opacity: 1;
      color: var(--accent-gold);
    }
  }

  &.active {
    background: rgba(254, 202, 0, 0.15);
    border-left: 3px solid var(--accent-gold);
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  border-radius: var(--radius-md);
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
  margin: 1rem;
  width: calc(100% - 2rem);
  background: rgba(200, 16, 46, 0.2);
  border: 1px solid rgba(200, 16, 46, 0.3);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: rgba(200, 16, 46, 0.3);
    color: var(--white);
  }
`;

const Main = styled.main`
  flex: 1;
  margin-left: 280px;
  padding: 2rem;

  @media (max-width: 1024px) {
    margin-left: 0;
    padding: 1.5rem;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  background: var(--white);
  padding: 1rem 1.5rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  color: var(--text-dark);
  padding: 0.5rem;
  border-radius: var(--radius-md);

  &:hover {
    background: var(--background-light);
  }

  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const PageTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 1.5rem;
  color: var(--text-dark);
  font-weight: 700;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  color: var(--text-medium);

  .avatar {
    width: 40px;
    height: 40px;
    background: var(--primary-blue);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--white);
    font-weight: 700;
  }
`;

const Overlay = styled.div`
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;

  @media (max-width: 1024px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

const AdminLayout = ({ children, title }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { isAuthenticated, logout, user } = useAdminAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/communiques', label: 'Communiqués', icon: FileText },
    { path: '/admin/articles', label: 'Articles', icon: FileEdit },
    { path: '/admin/galerie', label: 'Galerie', icon: Image },
    { path: '/admin/rapports', label: 'Rapports', icon: FileBarChart },
    { path: '/admin/etudes', label: 'Études', icon: BookMarked },
    { path: '/admin/plaintes', label: 'Réclamations', icon: MessageSquare },
    { path: '/admin/contacts', label: 'Messages', icon: Mail },
    { path: '/admin/conflits', label: 'Observatoire', icon: MapPin },
    { path: '/admin/utilisateurs', label: 'Utilisateurs', icon: Users },
    { path: '/admin/parametres', label: 'Contenus dynamiques', icon: Settings },
  ];

  return (
    <Layout>
      <Overlay $isOpen={isSidebarOpen} onClick={() => setIsSidebarOpen(false)} />
      
      <Sidebar $isOpen={isSidebarOpen}>
        <SidebarHeader>
          <div className="logo">
            <Shield size={28} />
          </div>
          <div>
            <h2>Admin</h2>
            <p>Médiature du Tchad</p>
          </div>
        </SidebarHeader>

        <Nav>
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <NavItem
                key={item.path}
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
                onClick={() => setIsSidebarOpen(false)}
              >
                <Icon size={20} />
                {item.label}
              </NavItem>
            );
          })}
        </Nav>

        <LogoutButton onClick={logout}>
          <LogOut size={20} />
          Déconnexion
        </LogoutButton>
      </Sidebar>

      <Main>
        <TopBar>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <MenuButton onClick={() => setIsSidebarOpen(true)}>
              <Menu size={24} />
            </MenuButton>
            <PageTitle>{title}</PageTitle>
          </div>
          <UserInfo>
            <div className="avatar">{user.email.charAt(0).toUpperCase() || 'A'}</div>
            <span>{user.email || 'Administrateur'}</span>
          </UserInfo>
        </TopBar>

        {children}
      </Main>
    </Layout>
  );
};

export default AdminLayout;
